import type { CSSProperties } from 'react';

/**
 * The nelnela mark — "signal" (Brand canvas 7a): a source dot and three sweeps leaving it,
 * each one the last said again, further and fuller. Geometry is copied from
 * `docs/design/asset-kit/mark.svg` rather than re-drawn; the asset kit's own rule is
 * "use them rather than re-drawing".
 *
 * It is inline SVG and not an <img> for one reason: the fill has to be `var(--nn-accent)` so
 * the mark darkens one step in the light scheme (#cd7a4d → #b8683f) like every other accent
 * in the app. An <img> would be stuck at the file's baked-in ember.
 *
 * Optical sizes are the kit's rule, not a preference (asset-kit/README.md § Optical sizes):
 * the ramp that tells the growth story at display sizes loses its first two ages on a dark rail
 * at 24px, so 20–40px gets the higher UI cut, and below 20px the mark recuts to two sweeps.
 */

const SWEEP_1 = 'M15 19 Q22 24 15 29 Q18.5 24 15 19 Z';
const SWEEP_2 = 'M23 13.5 Q34 24 23 34.5 Q28.5 24 23 13.5 Z';
const SWEEP_3 = 'M31 8 Q46 24 31 40 Q38.5 24 31 8 Z';
/** The two-sweep cut: the survivors, recentred. Never the full mark scaled down. */
const SMALL_SWEEP_1 = 'M17 13 Q29 24 17 35 Q23 24 17 13 Z';
const SMALL_SWEEP_2 = 'M27 7 Q44 24 27 41 Q35.5 24 27 7 Z';

/** 40px+ — the full ramp; the growth story reads at display size. */
const FULL_RAMP = { dot: 0.35, s1: 0.35, s2: 0.62, dotR: 2.5 } as const;
/** 20–40px — the UI cut (Brand 9b, adopted): starts higher so all four elements arrive. */
const UI_RAMP = { dot: 0.6, s1: 0.6, s2: 0.8, dotR: 2.7 } as const;

export type MarkCut = 'full' | 'ui' | 'two-sweep';

/** Which cut a given rendered size gets. Exported so the test can state the rule once. */
export function cutForSize(size: number): MarkCut {
  if (size < 20) return 'two-sweep';
  return size < 40 ? 'ui' : 'full';
}

export interface MarkProps {
  /** Rendered px. Picks the optical cut — 40+ full ramp, 20–40 UI cut, under 20 two sweeps. */
  size: number;
  title?: string;
  style?: CSSProperties;
}

/**
 * The static mark. `aria-hidden` unless given a `title`: in the rail and on the Sign In card it
 * sits beside the word "nelnela", and announcing it twice helps nobody.
 */
export function Mark({ size, title, style }: MarkProps) {
  const cut = cutForSize(size);
  const fill = 'var(--nn-accent)';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      style={{ flexShrink: 0, ...style }}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title && <title>{title}</title>}
      {cut === 'two-sweep' ? (
        <>
          <path d={SMALL_SWEEP_1} fill={fill} opacity={0.55} />
          <path d={SMALL_SWEEP_2} fill={fill} />
        </>
      ) : (
        <MarkBody ramp={cut === 'full' ? FULL_RAMP : UI_RAMP} fill={fill} />
      )}
    </svg>
  );
}

function MarkBody({ ramp, fill }: { ramp: typeof FULL_RAMP | typeof UI_RAMP; fill: string }) {
  return (
    <>
      <circle cx={7} cy={24} r={ramp.dotR} fill={fill} opacity={ramp.dot} />
      <path d={SWEEP_1} fill={fill} opacity={ramp.s1} />
      <path d={SWEEP_2} fill={fill} opacity={ramp.s2} />
      <path d={SWEEP_3} fill={fill} />
    </>
  );
}

/**
 * The brand loop — the sweeps leave the source one by one. Brand 7a runs it at 3.2s for its
 * motion tile, the Landing splash at 6.5s; same animation, different speed, hence the prop.
 *
 * Used for the boot splash (`Authed` while /api/me is in flight) and for waits of about a
 * second or more that are not content. Content gets skeletons — never a spinner, never this.
 */
export function MarkLoop({
  size,
  durationS = 6.5,
  style,
}: {
  size: number;
  durationS?: number;
  style?: CSSProperties;
}) {
  const fill = 'var(--nn-accent)';
  const anim = (name: string): CSSProperties => ({
    animation: `${name} ${durationS}s ease-out infinite`,
  });
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className="nn-mark-anim"
      style={{ flexShrink: 0, ...style }}
      aria-hidden
    >
      {/* The source is what the sweeps leave — it does not move. */}
      <circle cx={7} cy={24} r={2.5} fill={fill} opacity={0.35} />
      <path d={SWEEP_1} fill={fill} opacity={0.35} style={anim('nelS1')} />
      <path d={SWEEP_2} fill={fill} opacity={0.62} style={anim('nelS2')} />
      {/* nelS3 SCALES rather than fades, so it needs its own box and centre — without these two
          declarations it scales about the SVG's origin and visibly jumps. */}
      <path
        d={SWEEP_3}
        fill={fill}
        style={{ ...anim('nelS3'), transformBox: 'fill-box', transformOrigin: 'center' }}
      />
    </svg>
  );
}

/**
 * The completion motion — the only celebration this app has (design README §6: "played once,
 * on the spot"; the ring never laps and nothing else marks the moment). M5 builds it; M6a wires
 * it into Today's evening state.
 *
 * `animationIterationCount: 1` is the product rule, not a detail — the canvas loops it only so
 * the mockup keeps moving. The three sweeps start invisible and are built up by the keyframes,
 * then settle onto the UI-cut ramp (.6/.8/1) the static styles carry: when the animation ends
 * the mark is simply itself again, which is the resting state the ring wants beneath it.
 */
export function MarkCelebration({ size, style }: { size: number; style?: CSSProperties }) {
  const fill = 'var(--nn-accent)';
  const cel = (name: string): CSSProperties => ({
    animation: `${name} 3.4s ease-in-out`,
    animationIterationCount: 1,
  });
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className="nn-mark-anim"
      style={{ position: 'relative', flexShrink: 0, ...style }}
      aria-hidden
    >
      {/* Solid, unanimated: the source is already there — it is the arriving that is celebrated. */}
      <circle cx={7} cy={24} r={2.7} fill={fill} />
      <path d={SWEEP_1} fill={fill} opacity={0.6} style={cel('nelCel1')} />
      <path d={SWEEP_2} fill={fill} opacity={0.8} style={cel('nelCel2')} />
      <path d={SWEEP_3} fill={fill} style={cel('nelCel3')} />
    </svg>
  );
}

/**
 * The mark's keyframes, injected once globally beside READING_SURFACE_CSS (App.tsx). Copied
 * verbatim from the Brand canvas (nelS1–3) and the Today canvas (nelCel1–3).
 *
 * `prefers-reduced-motion` stops both: the loop becomes a still mark at its resting ramp and
 * the celebration simply does not play. Neither carries information — the ring and the "+N min"
 * line say everything the motion says.
 */
export const MARK_CSS = `
@keyframes nelS1{0%{opacity:.35}12%{opacity:1}26%,100%{opacity:.35}}
@keyframes nelS2{0%,14%{opacity:.62}28%{opacity:1}42%,100%{opacity:.62}}
@keyframes nelS3{0%,30%{transform:scale(1)}42%{transform:scale(1.1)}56%,100%{transform:scale(1)}}
@keyframes nelCel1{0%,6%{opacity:0}16%,78%{opacity:.6}92%,100%{opacity:0}}
@keyframes nelCel2{0%,14%{opacity:0}26%,78%{opacity:.8}92%,100%{opacity:0}}
@keyframes nelCel3{0%,22%{opacity:0}34%,78%{opacity:1}92%,100%{opacity:0}}
@media (prefers-reduced-motion: reduce) {
  .nn-mark-anim path { animation: none !important; }
}
`;
